// src/modulos/Desenvolvimento/SampleReportPage.js

import React, { useState, useMemo, useEffect } from 'react';
import { auth, db } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    query,
    orderBy,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    where
} from 'firebase/firestore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import {
    FormModal,
    ConfirmModal,
    PageHeader,
    Toast,
    Pagination,
    EmptyState
} from '../../components/common';
import styles from '../Registers/Registers.module.css';

// ============================================
// CONSTANTS
// ============================================
const PROJECTS_COLLECTION = 'development-projects';
const REPORT_HISTORY_COLLECTION = 'sample-report-history';
const ITEMS_PER_PAGE = 15;

// Project steps definition
const PROJECT_STEPS = [
    { id: 'formula', label: 'Formula Development', icon: '🧪' },
    { id: 'manipulation', label: 'Manipulation Process', icon: '🔬' },
    { id: 'quality', label: 'Quality Control', icon: '✓' },
    { id: 'client', label: 'Sent to Client', icon: '📦' },
    { id: 'status', label: 'Awaiting Approval', icon: '🏷️' },
];

const STATUS_OPTIONS = [
    { value: 'all', label: 'All Samples' },
    { value: 'formula', label: 'In Formula Development' },
    { value: 'manipulation', label: 'In Manipulation' },
    { value: 'quality', label: 'In Quality Control' },
    { value: 'client', label: 'Sent to Client' },
    { value: 'pending', label: 'Awaiting Approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
];

const SITUATION_OPTIONS = ['Active', 'On Hold', 'Completed', 'Cancelled'];

// ============================================
// STATUS BADGE COMPONENT
// ============================================
const SampleStatusBadge = ({ status }) => {
    const getStatusStyle = () => {
        switch (status) {
            case 'formula':
                return { background: '#cce5ff', color: '#004085', borderLeft: '3px solid #007bff' };
            case 'manipulation':
                return { background: '#fff3cd', color: '#856404', borderLeft: '3px solid #ffc107' };
            case 'quality':
                return { background: '#d1ecf1', color: '#0c5460', borderLeft: '3px solid #17a2b8' };
            case 'client':
                return { background: '#d6d8db', color: '#383d41', borderLeft: '3px solid #6c757d' };
            case 'pending':
                return { background: '#f8d7da', color: '#721c24', borderLeft: '3px solid #dc3545' };
            case 'approved':
                return { background: '#d4edda', color: '#155724', borderLeft: '3px solid #28a745' };
            case 'rejected':
                return { background: '#f8d7da', color: '#721c24', borderLeft: '3px solid #dc3545' };
            default:
                return { background: '#e9ecef', color: '#6c757d', borderLeft: '3px solid #6c757d' };
        }
    };

    const getStatusLabel = () => {
        switch (status) {
            case 'formula': return '🧪 Formula Development';
            case 'manipulation': return '🔬 Manipulation';
            case 'quality': return '✓ Quality Control';
            case 'client': return '📦 Sent to Client';
            case 'pending': return '🏷️ Awaiting Approval';
            case 'approved': return '✅ Approved';
            case 'rejected': return '❌ Rejected';
            default: return 'Unknown';
        }
    };

    return (
        <span className={styles.statusBadge} style={getStatusStyle()}>
            {getStatusLabel()}
        </span>
    );
};

// ============================================
// MAIN COMPONENT
// ============================================
const SampleReportPage = () => {
    // --- Permissions ---
    const can = usePagePermissions('listagem_de_amostras');

    // --- State ---
    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    // --- Filter States ---
    const [filters, setFilters] = useState({
        status: 'all',
        situation: '',
        projectName: '',
        clientName: '',
        dateFrom: '',
        dateTo: ''
    });

    // --- Pagination ---
    const [currentPage, setCurrentPage] = useState(1);

    // --- Data ---
    const { data: projects, loading: projectsLoading } = useFirestore(PROJECTS_COLLECTION, 'code');

    // --- Get Current User ---
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    // --- Load Report History ---
    useEffect(() => {
        loadReports();
    }, []);

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    const loadReports = async () => {
        setLoadingReports(true);
        try {
            const q = query(
                collection(db, REPORT_HISTORY_COLLECTION),
                orderBy('generatedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const reportsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                generatedAt: doc.data().generatedAt?.toDate() || new Date()
            }));
            setReports(reportsList);
        } catch (error) {
            console.error("Error loading report history:", error);
            addToast('Erro ao carregar histórico', 'error');
        } finally {
            setLoadingReports(false);
        }
    };

    const handleDeleteClick = (reportId) => {
        setReportToDelete(reportId);
        setIsConfirmOpen(true);
    };

    const deleteReport = async () => {
        if (!reportToDelete) return;

        try {
            await deleteDoc(doc(db, REPORT_HISTORY_COLLECTION, reportToDelete));
            setReports(reports.filter(r => r.id !== reportToDelete));
            addToast('Relatório excluído com sucesso', 'success');
        } catch (error) {
            console.error("Error deleting report:", error);
            addToast('Erro ao excluir relatório', 'error');
        } finally {
            setIsConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    // --- Extract Samples from Projects ---
    const allSamples = useMemo(() => {
        if (!projects) return [];

        const samples = [];

        projects.forEach(project => {
            if (!project.steps) return;

            const steps = project.steps;

            const getSampleStatus = () => {
                const allCompleted = PROJECT_STEPS.every(step =>
                    steps[step.id]?.status === 'completed' || steps[step.id]?.status === 'approved'
                );

                if (allCompleted && steps.status?.status === 'approved') {
                    return 'approved';
                }

                if (allCompleted && steps.status?.status === 'rejected') {
                    return 'rejected';
                }

                for (const step of PROJECT_STEPS) {
                    if (steps[step.id]?.status !== 'completed' && steps[step.id]?.status !== 'approved') {
                        return step.id;
                    }
                }

                if (allCompleted) {
                    return 'pending';
                }

                return 'formula';
            };

            const sampleStatus = getSampleStatus();

            const getStepDetails = () => {
                if (sampleStatus === 'approved' || sampleStatus === 'rejected' || sampleStatus === 'pending') {
                    return { date: steps.status?.date || null, notes: steps.status?.notes || '' };
                }

                const step = PROJECT_STEPS.find(s => s.id === sampleStatus);
                if (step && steps[step.id]) {
                    return {
                        date: steps[step.id].date || null,
                        notes: steps[step.id].notes || ''
                    };
                }
                return { date: null, notes: '' };
            };

            const stepDetails = getStepDetails();

            samples.push({
                projectId: project.id,
                projectCode: project.code || 'N/A',
                projectName: project.projectName || 'Untitled Project',
                clientName: project.clientName || 'Unknown Client',
                clientContact: project.clientContact || '',
                situation: project.situation || 'Active',
                sampleStatus: sampleStatus,
                statusDate: stepDetails.date,
                statusNotes: stepDetails.notes,
                createdAt: project.createdAt || null,
                briefing: project.briefing || {},
                productType: project.briefing?.productType || 'N/A',
                brandName: project.briefing?.brandName || 'N/A',
                targetAudience: project.briefing?.targetAudience || 'N/A',
            });
        });

        samples.sort((a, b) => (a.projectCode || '').localeCompare(b.projectCode || '', undefined, { numeric: true }));

        return samples;
    }, [projects]);

    // --- Apply Filters ---
    const filteredSamples = useMemo(() => {
        let filtered = [...allSamples];

        if (filters.status !== 'all') {
            filtered = filtered.filter(sample => sample.sampleStatus === filters.status);
        }

        if (filters.situation) {
            filtered = filtered.filter(sample => sample.situation === filters.situation);
        }

        if (filters.projectName) {
            const term = filters.projectName.toLowerCase();
            filtered = filtered.filter(sample =>
                sample.projectName.toLowerCase().includes(term) ||
                sample.projectCode.toLowerCase().includes(term)
            );
        }

        if (filters.clientName) {
            const term = filters.clientName.toLowerCase();
            filtered = filtered.filter(sample =>
                sample.clientName.toLowerCase().includes(term)
            );
        }

        if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(sample => {
                if (!sample.createdAt) return false;
                const created = new Date(sample.createdAt);
                return created >= fromDate;
            });
        }

        if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(sample => {
                if (!sample.createdAt) return false;
                const created = new Date(sample.createdAt);
                return created <= toDate;
            });
        }

        return filtered;
    }, [allSamples, filters]);

    // --- Pagination ---
    const totalPages = Math.ceil(filteredSamples.length / ITEMS_PER_PAGE);
    const currentSamples = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredSamples.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredSamples, currentPage]);

    // --- Filter Handlers ---
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters({
            status: 'all',
            situation: '',
            projectName: '',
            clientName: '',
            dateFrom: '',
            dateTo: ''
        });
        setCurrentPage(1);
    };

    // --- Generate Report ---
    const generateReport = async () => {
        if (!currentUser) {
            addToast('Você precisa estar logado para gerar relatórios', 'error');
            return;
        }

        if (filteredSamples.length === 0) {
            addToast('Nenhuma amostra encontrada com os filtros selecionados.', 'warning');
            return;
        }

        setGenerating(true);
        addToast('Gerando relatório...', 'info');

        try {
            const filterDisplay = {};
            if (filters.status !== 'all') {
                const statusMap = {
                    formula: 'Formula Development',
                    manipulation: 'Manipulation',
                    quality: 'Quality Control',
                    client: 'Sent to Client',
                    pending: 'Awaiting Approval',
                    approved: 'Approved',
                    rejected: 'Rejected'
                };
                filterDisplay.statusLabel = statusMap[filters.status] || filters.status;
            }
            if (filters.situation) filterDisplay.situation = filters.situation;
            if (filters.projectName) filterDisplay.projectName = filters.projectName;
            if (filters.clientName) filterDisplay.clientName = filters.clientName;
            if (filters.dateFrom) filterDisplay.dateFrom = new Date(filters.dateFrom).toLocaleDateString('pt-BR');
            if (filters.dateTo) filterDisplay.dateTo = new Date(filters.dateTo).toLocaleDateString('pt-BR');

            let reportTitle = 'RELATÓRIO DE AMOSTRAS';
            if (filterDisplay.statusLabel) reportTitle += ` - ${filterDisplay.statusLabel}`;

            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filterParts = [];
            if (filterDisplay.statusLabel) filterParts.push(filterDisplay.statusLabel.replace(/\s/g, '_'));
            const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
            const fileName = `Relatorio_Amostras${filterSuffix}_${timestamp}.xlsx`;

            await generateExcelReport(filteredSamples, filterDisplay, reportTitle, fileName);

            const reportData = {
                fileName,
                generatedAt: serverTimestamp(),
                generatedBy: currentUser.email || currentUser.displayName || currentUser.uid,
                generatedByEmail: currentUser.email,
                generatedByName: currentUser.displayName || '',
                sampleCount: filteredSamples.length,
                filters: {
                    status: filters.status,
                    statusLabel: filterDisplay.statusLabel || null,
                    situation: filters.situation || null,
                    projectName: filters.projectName || null,
                    clientName: filters.clientName || null,
                    dateFrom: filters.dateFrom || null,
                    dateTo: filters.dateTo || null
                }
            };

            const docRef = await addDoc(collection(db, REPORT_HISTORY_COLLECTION), reportData);

            setReports([
                {
                    id: docRef.id,
                    ...reportData,
                    generatedAt: new Date()
                },
                ...reports
            ]);

            addToast(`Relatório "${fileName}" gerado com sucesso!`, 'success');
            setIsModalOpen(false);

        } catch (error) {
            console.error('Error generating report:', error);
            addToast(`Erro ao gerar relatório: ${error.message}`, 'error');
        } finally {
            setGenerating(false);
        }
    };

    // --- Generate Excel ---
    const generateExcelReport = async (items, filtersObj, reportTitle, fileName) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Amostras');

        worksheet.mergeCells('A1:H1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = reportTitle;
        titleCell.font = { size: 16, bold: true };
        titleCell.alignment = { horizontal: 'center' };

        worksheet.mergeCells('A2:H2');
        worksheet.getCell('A2').value = `Gerado em: ${new Date().toLocaleString('pt-BR')}`;
        worksheet.getCell('A2').font = { size: 10, italic: true };

        worksheet.mergeCells('A3:H3');
        worksheet.getCell('A3').value = `Gerado por: ${currentUser?.email || 'Usuário'}`;
        worksheet.getCell('A3').font = { size: 10, italic: true };

        let filterRow = 4;
        const filterParts = [];
        if (filtersObj.statusLabel) filterParts.push(`Status: ${filtersObj.statusLabel}`);
        if (filtersObj.situation) filterParts.push(`Situação: ${filtersObj.situation}`);
        if (filtersObj.projectName) filterParts.push(`Projeto: ${filtersObj.projectName}`);
        if (filtersObj.clientName) filterParts.push(`Cliente: ${filtersObj.clientName}`);
        if (filtersObj.dateFrom) filterParts.push(`De: ${filtersObj.dateFrom}`);
        if (filtersObj.dateTo) filterParts.push(`Até: ${filtersObj.dateTo}`);

        if (filterParts.length > 0) {
            worksheet.mergeCells(`A${filterRow}:H${filterRow}`);
            worksheet.getCell(`A${filterRow}`).value = `Filtros: ${filterParts.join(' | ')}`;
            worksheet.getCell(`A${filterRow}`).font = { size: 10, italic: true };
            filterRow++;
        }

        filterRow++;

        const headers = [
            'Código Projeto',
            'Nome Projeto',
            'Cliente',
            'Status Amostra',
            'Data Status',
            'Situação Projeto',
            'Tipo Produto',
            'Marca'
        ];
        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF2C3E50' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
        worksheet.getRow(filterRow).height = 35;

        worksheet.getColumn(1).width = 15;
        worksheet.getColumn(2).width = 30;
        worksheet.getColumn(3).width = 30;
        worksheet.getColumn(4).width = 25;
        worksheet.getColumn(5).width = 18;
        worksheet.getColumn(6).width = 15;
        worksheet.getColumn(7).width = 20;
        worksheet.getColumn(8).width = 20;

        items.forEach((sample) => {
            const statusLabels = {
                formula: 'Formula Development',
                manipulation: 'Manipulation',
                quality: 'Quality Control',
                client: 'Sent to Client',
                pending: 'Awaiting Approval',
                approved: 'Approved',
                rejected: 'Rejected'
            };

            const row = worksheet.addRow([
                sample.projectCode,
                sample.projectName,
                sample.clientName,
                statusLabels[sample.sampleStatus] || sample.sampleStatus,
                sample.statusDate ? new Date(sample.statusDate).toLocaleDateString('pt-BR') : 'N/A',
                sample.situation,
                sample.productType || 'N/A',
                sample.brandName || 'N/A'
            ]);

            row.eachCell((cell, colNumber) => {
                cell.alignment = { vertical: 'middle', wrapText: true };
                if (colNumber !== 2 && colNumber !== 3) {
                    cell.alignment = { ...cell.alignment, horizontal: 'center' };
                }
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });
            row.height = 30;
        });

        const summaryRowIndex = worksheet.rowCount + 1;
        const summaryRow = worksheet.addRow([`Total de amostras: ${items.length}`, '', '', '', '', '', '', '']);
        summaryRow.getCell(1).font = { bold: true };
        worksheet.mergeCells(`A${summaryRowIndex}:H${summaryRowIndex}`);
        worksheet.getRow(summaryRowIndex).height = 28;

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    // --- Format Date ---
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // --- Get Filter Description for History ---
    const getFilterDescription = (filtersObj) => {
        if (!filtersObj) return '-';
        const parts = [];
        if (filtersObj.statusLabel) parts.push(`Status: ${filtersObj.statusLabel}`);
        if (filtersObj.situation) parts.push(`Situação: ${filtersObj.situation}`);
        if (filtersObj.projectName) parts.push(`Projeto: ${filtersObj.projectName}`);
        if (filtersObj.clientName) parts.push(`Cliente: ${filtersObj.clientName}`);
        if (filtersObj.dateFrom) parts.push(`De: ${filtersObj.dateFrom}`);
        if (filtersObj.dateTo) parts.push(`Até: ${filtersObj.dateTo}`);
        if (parts.length === 0) return 'Todas as amostras';
        return parts.join(', ');
    };

    // --- Open/Close Modal ---
    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        handleResetFilters();
    };

    // --- Check if any filter is active ---
    const hasActiveFilters = filters.status !== 'all' ||
        filters.situation ||
        filters.projectName ||
        filters.clientName ||
        filters.dateFrom ||
        filters.dateTo;

    const isLoading = projectsLoading || loadingReports;

    // Render filter form content for modal
    const renderFilterForm = () => (
        <>
            <div className={styles.formGroup}>
                <label>Status da Amostra</label>
                <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className={styles.select}
                >
                    {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            <div className={styles.formGroup}>
                <label>Situação do Projeto</label>
                <select
                    value={filters.situation}
                    onChange={(e) => handleFilterChange('situation', e.target.value)}
                    className={styles.select}
                >
                    <option value="">Todas as situações</option>
                    {SITUATION_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            </div>

            <div className={styles.twoColumnGrid}>
                <div className={styles.formGroup}>
                    <label>Buscar por Projeto</label>
                    <input
                        type="text"
                        placeholder="Código ou nome do projeto..."
                        value={filters.projectName}
                        onChange={(e) => handleFilterChange('projectName', e.target.value)}
                        className={styles.input}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Buscar por Cliente</label>
                    <input
                        type="text"
                        placeholder="Nome do cliente..."
                        value={filters.clientName}
                        onChange={(e) => handleFilterChange('clientName', e.target.value)}
                        className={styles.input}
                    />
                </div>
            </div>

            <div className={styles.twoColumnGrid}>
                <div className={styles.formGroup}>
                    <label>Data Cadastro (Início)</label>
                    <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className={styles.input}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Data Cadastro (Fim)</label>
                    <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className={styles.input}
                    />
                </div>
            </div>
        </>
    );

    // ============================================
    // RENDER
    // ============================================
    return (
        <div className={styles.pageContainer}>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={deleteReport}
                title="Excluir Relatório"
                message="Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita."
                confirmText="Excluir"
                danger
            />

            {/* Filter Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={generateReport}
                title="Gerar Relatório de Amostras"
                subtitle="Selecione os filtros desejados para o relatório"
                isLoading={generating}
                submitText={`Gerar Relatório (${filteredSamples.length})`}
                cancelText="Cancelar"
                maxWidth="700px"
            >
                {renderFilterForm()}
                <div className={styles.formActions} style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                    <button type="button" onClick={handleResetFilters} className={styles.secondaryButton}>
                        Limpar Filtros
                    </button>
                </div>
            </FormModal>

            {/* Main Header */}
            <PageHeader
                title="Relatório de Amostras"
                actionText="Gerar Relatório"
                onAction={openModal}
                disabled={!currentUser || isLoading || !can.create}
            />

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <div className={styles.activeFiltersContainer}>
                    <span className={styles.activeFiltersLabel}>Filtros ativos:</span>
                    {filters.status !== 'all' && (
                        <span className={styles.filterBadge}>
                            📌 Status: {STATUS_OPTIONS.find(s => s.value === filters.status)?.label}
                        </span>
                    )}
                    {filters.situation && (
                        <span className={styles.filterBadge}>
                            📋 Situação: {filters.situation}
                        </span>
                    )}
                    {filters.projectName && (
                        <span className={styles.filterBadge}>
                            🔍 Projeto: {filters.projectName}
                        </span>
                    )}
                    {filters.clientName && (
                        <span className={styles.filterBadge}>
                            🏢 Cliente: {filters.clientName}
                        </span>
                    )}
                    {filters.dateFrom && (
                        <span className={styles.filterBadge}>
                            📅 De: {new Date(filters.dateFrom).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                    {filters.dateTo && (
                        <span className={styles.filterBadge}>
                            📅 Até: {new Date(filters.dateTo).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                </div>
            )}

            

            {/* Report History */}
            <div className={styles.tableContainer} style={{ marginTop: '32px' }}>
                <h3 className={styles.tableTitle} style={{ padding: '16px 20px', margin: 0, borderBottom: '1px solid var(--border-color)' }}>
                    Histórico de Relatórios Gerados
                </h3>
                <div style={{ overflowX: 'auto' }}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '160px' }}>Data/Hora</th>
                                <th>Nome do Arquivo</th>
                                <th style={{ width: '200px' }}>Gerado por</th>
                                <th style={{ width: '80px' }}>Amostras</th>
                                <th style={{ width: '250px' }}>Filtros Aplicados</th>
                                <th style={{ width: '80px' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingReports ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        <div className={styles.spinner} style={{ margin: '0 auto' }}></div>
                                        <span style={{ marginTop: '8px', display: 'block' }}>Carregando histórico...</span>
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        Nenhum relatório gerado ainda. Clique em "Gerar Relatório" para começar.
                                    </td>
                                </tr>
                            ) : (
                                reports.map((report) => (
                                    <tr key={report.id} className={styles.tableRow}>
                                        <td>{formatDate(report.generatedAt)}</td>
                                        <td>
                                            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                                {report.fileName}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <span style={{ fontWeight: 500 }}>
                                                    {report.generatedByName || report.generatedBy?.split('@')[0] || 'Usuário'}
                                                </span>
                                                {report.generatedByEmail && (
                                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                                        {report.generatedByEmail}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{report.sampleCount}</td>
                                        <td style={{ fontSize: '12px' }}>
                                            {getFilterDescription(report.filters)}
                                        </td>
                                        <td>
                                            {can.delete && (
                                                <button
                                                    onClick={() => handleDeleteClick(report.id)}
                                                    className={`${styles.actionButton} ${styles.deleteButton}`}
                                                    style={{ padding: '4px 12px', fontSize: '12px' }}
                                                >
                                                    Excluir
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                {totalPages > 1 && !isLoading && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={filteredSamples.length}
                        onPageChange={setCurrentPage}
                        itemsLabel="amostras"
                    />
                )}
            </div>
        </div>
    );
};

export default SampleReportPage;